/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import Modal from '../../../../../components/Modal/Modal';
import { PrimaryButton, GhostButton } from '../../../../../styles/ui/Button';
import { updateManualSteps } from '../../../../../services/hr/recruitment/onboardingApi';
import {
   ONBOARDING_STATUS,
   ONBOARDING_STATUS_LABEL,
} from '../../constants';
import { recruitmentErrorMessage } from '../../utils/apiError';
import { manualStepDraft, manualStepPatch } from '../manualSteps';
import ManualStepFields from './ManualStepFields';
import ExamDocumentField from './ExamDocumentField';

// ---------------------------------------------------------------------------
// Edição RETROATIVA de um passo manual já concluído (aberto pelo ícone de
// editar no rail "Progresso da admissão") — mesmos campos do painel da etapa,
// via config compartilhada. Corrige erro sem quebrar o fluxo visual do design.
// ---------------------------------------------------------------------------

export default function ManualStepModal({
   stepKey,
   onClose,
   onboarding,
   onSaved,
}) {
   const isOpen = Boolean(stepKey);
   const baseline = useMemo(
      () => (stepKey ? manualStepDraft(stepKey, onboarding) : {}),
      [stepKey, onboarding],
   );
   const [draft, setDraft] = useState(baseline);
   const [isSaving, setIsSaving] = useState(false);

   // Reset por VALOR (ver StepWorkPanel): refetch sem mudança nos campos
   // (ex.: upload do laudo dentro do modal) não apaga edição não-salva.
   const baselineJson = JSON.stringify(baseline);
   useEffect(() => {
      if (isOpen) {
         setDraft(JSON.parse(baselineJson));
         setIsSaving(false);
      }
   }, [isOpen, baselineJson]);

   if (!isOpen) return null;

   const isDirty = Object.keys(baseline).some(
      (key) => draft[key] !== baseline[key],
   );

   const handleSave = async () => {
      if (isSaving || !isDirty) return;
      setIsSaving(true);
      try {
         const { message } = await updateManualSteps(
            onboarding.id,
            manualStepPatch(stepKey, draft, baseline),
         );
         toast.success(message);
         onSaved?.();
         onClose?.();
      } catch (err) {
         toast.error(
            recruitmentErrorMessage(err, 'Erro ao salvar o passo manual.'),
         );
      } finally {
         setIsSaving(false);
      }
   };

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title={`Editar · ${ONBOARDING_STATUS_LABEL[stepKey] ?? stepKey}`}
         maxWidth="640px"
         footer={
            <>
               <GhostButton
                  type="button"
                  onClick={onClose}
                  disabled={isSaving}
               >
                  Cancelar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving || !isDirty}
               >
                  {isSaving ? 'Salvando…' : 'Salvar'}
               </PrimaryButton>
            </>
         }
      >
         <ManualStepFields
            stepKey={stepKey}
            draft={draft}
            onChange={(key, value) =>
               setDraft((prev) => ({ ...prev, [key]: value }))
            }
            disabled={isSaving}
            idPrefix="step-modal"
         >
            {stepKey === ONBOARDING_STATUS.EXAME_ADMISSIONAL && (
               <ExamDocumentField
                  onboarding={onboarding}
                  canAct={!isSaving}
                  onChanged={onSaved}
               />
            )}
         </ManualStepFields>
      </Modal>
   );
}

ManualStepModal.propTypes = {
   stepKey: PropTypes.string,
   onClose: PropTypes.func.isRequired,
   onboarding: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   }),
   onSaved: PropTypes.func,
};
